import {
    Box, Card, Typography, Divider, Button, Grid, TextField, InputAdornment, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress,
} from '@mui/material';
import { useState } from 'react';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import COLORS from '../../../../../constants/colors';
import { toast } from 'react-toastify';
import baseURL from '../../../user/config';

// The component now accepts userProfile and onDataUpdated as props
const AccountManagementPage = ({ userProfile, onDataUpdated }) => {
    const [editProfileForm, setEditProfileForm] = useState(userProfile);
    const [isEditProfileDialogOpen, setIsEditProfileDialogOpen] = useState(false);
    const [passwordForm, setPasswordForm] = useState({
        currentPassword: '', newPassword: '', confirmPassword: '',
    });
    const [submitting, setSubmitting] = useState(false);
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const handleEditProfileChange = (e) => {
        const { name, value } = e.target;
        setEditProfileForm((prevForm) => ({
            ...prevForm,
            [name]: value,
        }));
    };

    const handleEditProfileSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const response = await fetch(`${baseURL}/org/profile`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name: editProfileForm.name }),
                credentials: 'include',
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to update profile.');
            }

            const result = await response.json();
            setIsEditProfileDialogOpen(false);
            toast.success(result.message);
            onDataUpdated(); // Notify the parent to re-fetch the data
        } catch (error) {
            console.error('Profile update error:', error);
            toast.error(error.message);
        } finally {
            setSubmitting(false);
        }
    };

    const handlePasswordChange = (e) => {
        const { name, value } = e.target;
        setPasswordForm((prevForm) => ({ ...prevForm, [name]: value }));
    };

    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        if (passwordForm.newPassword !== passwordForm.confirmPassword) {
            toast.error('New passwords do not match.');
            return;
        }
        setSubmitting(true);
        try {
            const response = await fetch(`${baseURL}/org/password`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    currentPassword: passwordForm.currentPassword,
                    newPassword: passwordForm.newPassword,
                }),
                credentials: 'include',
            });

            const result = await response.json();
            if (!response.ok) {
                throw new Error(result.message || 'Failed to update password.');
            }

            toast.success(result.message);
            setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
        } catch (error) {
            console.error('Password update error:', error);
            toast.error(error.message);
        } finally {
            setSubmitting(false);
        }
    };

    const handleClickShowPassword = (field) => {
        switch (field) {
            case 'current':
                setShowCurrentPassword(!showCurrentPassword);
                break;
            case 'new':
                setShowNewPassword(!showNewPassword);
                break;
            case 'confirm':
                setShowConfirmPassword(!showConfirmPassword);
                break;
            default:
                break;
        }
    };

    // The rest of the component's JSX remains largely the same, but it uses the userProfile prop.
    return (
        <Box sx={{ mt: 3, mx: 'auto' }}>
            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <Card sx={{ backgroundColor: '#fff', borderRadius: '16px', boxShadow: '0 2px 16px rgba(0,0,0,0.06)', border: '1px solid rgba(0,0,0,0.05)', p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <AccountCircleOutlinedIcon sx={{ fontSize: '1.6rem', mr: 1.5, color: '#2D4A3D' }} />
                            <Typography variant="h6" sx={{ fontWeight: 800, color: '#1a1a2e', fontSize: '1.05rem', letterSpacing: '-0.3px' }}>Profile Information</Typography>
                        </Box>
                        <Divider sx={{ mb: 3 }} />
                        <Box>
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>Full Name</Typography>
                                <Typography variant="body1" fontWeight="bold">{userProfile.name}</Typography>
                            </Box>
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>Email Address</Typography>
                                <Typography variant="body1" fontWeight="bold">{userProfile.email}</Typography>
                            </Box>
                        </Box>
                        <Button variant="outlined" startIcon={<EditOutlinedIcon />} onClick={() => setIsEditProfileDialogOpen(true)} sx={{ mt: 3, textTransform: 'none' }}>Edit Profile</Button>
                    </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Card component="form" onSubmit={handlePasswordSubmit} sx={{ backgroundColor: '#fff', borderRadius: '16px', boxShadow: '0 2px 16px rgba(0,0,0,0.06)', border: '1px solid rgba(0,0,0,0.05)', p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <LockOutlinedIcon sx={{ fontSize: '1.6rem', mr: 1.5, color: '#2D4A3D' }} />
                            <Typography variant="h6" sx={{ fontWeight: 800, color: '#1a1a2e', fontSize: '1.05rem', letterSpacing: '-0.3px' }}>Change Password</Typography>
                        </Box>
                        <Divider sx={{ mb: 3 }} />
                        <Box>
                            <TextField label="Current Password" type={showCurrentPassword ? 'text' : 'password'} name="currentPassword" value={passwordForm.currentPassword} onChange={handlePasswordChange} fullWidth margin="normal" required
                                InputProps={{
                                    endAdornment: (<InputAdornment position="end">
                                        <IconButton onClick={() => handleClickShowPassword('current')} edge="end">
                                            {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>),
                                }}
                            />
                            <TextField label="New Password" type={showNewPassword ? 'text' : 'password'} name="newPassword" value={passwordForm.newPassword} onChange={handlePasswordChange} fullWidth margin="normal" required
                                InputProps={{
                                    endAdornment: (<InputAdornment position="end">
                                        <IconButton onClick={() => handleClickShowPassword('new')} edge="end">
                                            {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>),
                                }}
                            />
                            <TextField label="Confirm New Password" type={showConfirmPassword ? 'text' : 'password'} name="confirmPassword" value={passwordForm.confirmPassword} onChange={handlePasswordChange} fullWidth margin="normal" required
                                InputProps={{
                                    endAdornment: (<InputAdornment position="end">
                                        <IconButton onClick={() => handleClickShowPassword('confirm')} edge="end">
                                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>),
                                }}
                            />
                        </Box>
                        <Button type="submit" variant="contained" disabled={submitting} sx={{ textTransform: 'none', boxShadow: 0, mt: 3, backgroundColor: '#2D4A3D', '&:hover': { backgroundColor: '#2D4A3D', opacity: 0.9, boxShadow: 0, }, }}>
                            {submitting ? (<><CircularProgress size={18} sx={{ mr: 1 }} /> Just a sec...</>) : ('Update password')}
                        </Button>
                    </Card>
                </Grid>
            </Grid>
            <Dialog open={isEditProfileDialogOpen} onClose={() => setIsEditProfileDialogOpen(false)}>
                <DialogTitle sx={{ fontWeight: 'bold' }}>Edit Profile Information</DialogTitle>
                <form onSubmit={handleEditProfileSubmit}>
                    <DialogContent>
                        <TextField autoFocus margin="dense" label="Full Name" type="text" name="name" fullWidth variant="outlined" value={editProfileForm.name || ''} onChange={handleEditProfileChange} />
                        <TextField margin="dense" label="Email Address" type="email" name="email" fullWidth variant="outlined" value={editProfileForm.email || ''} onChange={handleEditProfileChange} disabled />
                    </DialogContent>
                    <DialogActions sx={{ p: 3 }}>
                        <Button onClick={() => setIsEditProfileDialogOpen(false)} color="secondary" sx={{ textTransform: 'none', boxShadow: 0 }}>Cancel</Button>
                        <Button type="submit" variant="contained" disabled={submitting} sx={{ textTransform: 'none', boxShadow: 0, backgroundColor: '#2D4A3D', '&:hover': { backgroundColor: '#2D4A3D', opacity: 0.9, boxShadow: 0, }, }}>
                            {submitting ? (<><CircularProgress size={18} sx={{ mr: 1 }} /> Just a sec...</>) : ('Save Changes')}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>
        </Box>
    );
};

export default AccountManagementPage;